import React, { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import WebRTCService from '../services/WebRTCService';
import FileTransferService from '../services/FileTransferService';

const SendPage = () => {
  const [files, setFiles] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('idle'); // idle, connecting, waiting, connected, transferring, completed
  const [transferProgress, setTransferProgress] = useState({ totalProgress: 0 });
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const fileInputRef = useRef(null);
  const webrtcRef = useRef(null);
  const fileTransferRef = useRef(null);

  // Generate a unique room ID
  const generateRoomId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const startSharing = async () => {
    if (files.length === 0) return;

    try {
      setConnectionStatus('connecting');
      
      // Generate room ID
      const newRoomId = generateRoomId();
      setRoomId(newRoomId);

      // Create share URL
      const url = `${window.location.origin}/receive/${newRoomId}`;
      setShareUrl(url);

      // Initialize WebRTC
      webrtcRef.current = new WebRTCService();
      await webrtcRef.current.connect('ws://localhost:3001');
      await webrtcRef.current.createRoom(newRoomId, 'Sender');

      // Set up connection state handler
      webrtcRef.current.onConnectionChange((state) => {
        console.log('Connection state:', state);
        if (state === 'connected') {
          setConnectionStatus('connected');
          // Start file transfer
          startFileTransfer();
        } else if (state === 'disconnected') {
          setConnectionStatus('idle');
        }
      });

      setConnectionStatus('waiting');
    } catch (error) {
      console.error('Error starting share:', error);
      setConnectionStatus('idle');
      alert('Failed to start sharing. Please try again.');
    }
  };

  const startFileTransfer = () => {
    if (!webrtcRef.current) return;

    setConnectionStatus('transferring');

    fileTransferRef.current = new FileTransferService(webrtcRef.current);
    
    fileTransferRef.current.onProgress((progress) => {
      setTransferProgress(progress);
    });

    fileTransferRef.current.onComplete(() => {
      setConnectionStatus('completed');
    });

    fileTransferRef.current.sendFiles(files);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTotalSize = () => {
    return files.reduce((total, file) => total + file.size, 0);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (webrtcRef.current) {
        webrtcRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 text-center mb-8">
        Share Your Files
      </h1>

      {/* File Selection Area */}
      {connectionStatus === 'idle' && (
        <>
          <div
            className="border-2 border-dashed border-indigo-300 rounded-xl p-12 text-center hover:border-indigo-500 transition bg-white cursor-pointer"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            
            <div className="text-6xl mb-4">📁</div>
            <p className="text-xl text-gray-700 mb-2">
              Drag & Drop files here
            </p>
            <p className="text-sm text-gray-500">
              or click to select files
            </p>
          </div>

          {/* Selected Files List */}
          {files.length > 0 && (
            <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Selected Files ({files.length})
                </h3>
                <span className="text-sm text-gray-500">
                  Total: {formatFileSize(getTotalSize())}
                </span>
              </div>

              <div className="space-y-3 max-h-64 overflow-y-auto">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">📄</span>
                      <div>
                        <p className="font-medium text-gray-900 truncate max-w-xs">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              {/* Password Protection */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showPassword}
                    onChange={(e) => setShowPassword(e.target.checked)}
                    className="rounded text-indigo-600"
                  />
                  <span className="text-sm text-gray-700">Password Protection (Optional)</span>
                </label>
                
                {showPassword && (
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password..."
                    className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>

              <button
                onClick={startSharing}
                className="mt-6 w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
              >
                Start Sharing
              </button>
            </div>
          )}
        </>
      )}

      {/* Connecting Status */}
      {connectionStatus === 'connecting' && (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="animate-spin text-5xl mb-4">⏳</div>
          <h2 className="text-xl font-semibold text-gray-900">Setting up connection...</h2>
          <p className="text-gray-500 mt-2">Please wait while we prepare your share link</p>
        </div>
      )}

      {/* Waiting for Receiver */}
      {connectionStatus === 'waiting' && (
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">🔗</div>
            <h2 className="text-xl font-semibold text-gray-900">Ready to Share!</h2>
            <p className="text-gray-500 mt-2">Share this code or link with the receiver</p>
          </div>

          {/* Share Code */}
          <div className="bg-gray-100 rounded-lg p-6 text-center mb-6">
            <p className="text-sm text-gray-500 mb-2">Share Code</p>
            <div className="text-4xl font-mono font-bold text-indigo-600 tracking-widest">
              {roomId}
            </div>
          </div>

          {/* QR Code */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-white rounded-lg shadow-sm">
              <QRCodeSVG value={shareUrl} size={180} />
            </div>
          </div>

          {/* Share Link */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-500 mb-2">Or share this link</p>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 bg-white px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <button
                onClick={() => copyToClipboard(shareUrl)}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700"
              >
                {copied ? '✓ Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-center space-x-2 text-amber-600 bg-amber-50 rounded-lg p-3">
            <span>⚠️</span>
            <span className="text-sm">Keep this tab open until transfer is complete</span>
          </div>
        </div>
      )}

      {/* Connected - Transferring */}
      {(connectionStatus === 'connected' || connectionStatus === 'transferring') && (
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">📤</div>
            <h2 className="text-xl font-semibold text-gray-900">Transferring Files...</h2>
            <p className="text-gray-500 mt-2">Sending directly to receiver</p>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>{transferProgress.fileName || 'Starting...'}</span>
              <span>{Math.round(transferProgress.totalProgress || 0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className="bg-indigo-600 h-4 rounded-full transition-all duration-300"
                style={{ width: `${transferProgress.totalProgress || 0}%` }}
              />
            </div>
          </div>

          <div className="text-center text-sm text-gray-500">
            {transferProgress.bytesTransferred && transferProgress.totalBytes && (
              <span>
                {formatFileSize(transferProgress.bytesTransferred)} / {formatFileSize(transferProgress.totalBytes)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Completed */}
      {connectionStatus === 'completed' && (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-green-600">Transfer Complete!</h2>
          <p className="text-gray-500 mt-2 mb-6">
            All {files.length} file(s) have been successfully transferred.
          </p>
          <button
            onClick={() => {
              setFiles([]);
              setRoomId('');
              setConnectionStatus('idle');
              setTransferProgress({ totalProgress: 0 });
              if (webrtcRef.current) {
                webrtcRef.current.disconnect();
              }
            }}
            className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-700"
          >
            Share More Files
          </button>
        </div>
      )}
    </div>
  );
};

export default SendPage;
